import { Anchor, Center, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { IconCloudOff } from '@tabler/icons-react';
import { useUploadStore } from '../uploadStore';
import { UploadFileItem } from './UploadFileItem';

interface UploadFileListProps {
  onCancel: (uppyFileId: string) => void;
  onRetry: (uppyFileId: string) => void;
  /** Resume every failed row from its uploaded parts. */
  onRetryAllFailed: () => void;
  /** Engine-backed clear: also releases the files from Uppy state. */
  onClearCompleted: () => void;
}

const ACTIVE_STATUSES = new Set(['pending', 'creating', 'uploading', 'processing']);

export function UploadFileList({
  onCancel,
  onRetry,
  onRetryAllFailed,
  onClearCompleted,
}: UploadFileListProps) {
  const items = useUploadStore((s) => s.items);

  if (items.length === 0) {
    return (
      <Center py="xl" style={{ flex: 1 }}>
        <Stack align="center" gap="xs">
          <IconCloudOff size={32} color="var(--mantine-color-gray-4)" />
          <Text size="sm" c="dimmed">
            No uploads yet
          </Text>
        </Stack>
      </Center>
    );
  }

  // Active items first, then terminal (ready/error/cancelled)
  const sorted = [...items].sort((a, b) => {
    const aActive = ACTIVE_STATUSES.has(a.status) ? 0 : 1;
    const bActive = ACTIVE_STATUSES.has(b.status) ? 0 : 1;
    return aActive - bActive;
  });

  const hasTerminal = items.some(
    (i) => i.status === 'ready' || i.status === 'error' || i.status === 'cancelled'
  );
  const failedCount = items.filter((i) => i.status === 'error' && i.uppyFileId).length;

  return (
    <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
      {hasTerminal && (
        <Group justify="flex-end" gap="md">
          {failedCount > 0 && (
            <Anchor size="xs" c="orange.8" fw={600} onClick={onRetryAllFailed}>
              Retry failed ({failedCount})
            </Anchor>
          )}
          <Anchor size="xs" onClick={onClearCompleted}>
            Clear completed
          </Anchor>
        </Group>
      )}
      <ScrollArea style={{ flex: 1 }}>
        <Stack gap="xs">
          {sorted.map((item) => (
            <UploadFileItem
              key={item.id}
              item={item}
              onCancel={onCancel}
              onRetry={onRetry}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
